// プレビュー種別の明示的な選択（自動・不可視の降格はしない）。
// 完全検証は NAS エージェント v1.3.2 のカーソルページングが必要。
// 未対応のときはボタンを押せる状態にせず、理由を日英で明示する。
import { Button } from '@/components/ui/button';
import { Eye, ListChecks, Warning, Info } from '@phosphor-icons/react';

const MODE_FULL = 'full_validation';
const MODE_ESTIMATE = 'quick_estimate';

const findMode = (modes, key) => (modes?.modes || []).find((m) => m.mode === key) || null;

export function NasPreviewModePanel({ modes, connected, busy, activeMode, onStart,
  lastKnownVersion }) {
  const full = findMode(modes, MODE_FULL);
  const estimate = findMode(modes, MODE_ESTIMATE);
  const pagination = !!modes?.pagination;
  const reachable = modes ? modes.agent_reachable !== false : false;
  const fullAvailable = !!full?.available && connected && reachable;
  const estimateAvailable = estimate ? estimate.available !== false && connected : connected;

  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 px-3 py-2.5"
      data-testid="nas-preview-mode-panel" data-pagination={pagination ? 'true' : 'false'}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[12px] font-semibold text-zinc-700">
          プレビューの種類をえらぶ
          <span className="ml-1 text-[10.5px] font-normal text-zinc-500">Choose a preview type</span>
        </p>
        <div className="flex-1" />
        <span className="text-[10.5px] px-2 py-0.5 rounded-full border border-zinc-300 bg-white text-zinc-700"
          data-testid="nas-preview-agent-info" data-agent-version={modes?.agent_version || ''}>
          エージェント / Agent {modes?.agent_version ? `v${modes.agent_version}` : '未取得 / unknown'}
          {!modes?.agent_version && lastKnownVersion && (
            <span className="ml-1 opacity-70">（直近の実行時 v{lastKnownVersion} / last run）</span>
          )}
        </span>
        <span className={`text-[10.5px] px-2 py-0.5 rounded-full border font-semibold ${
          pagination ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
            : 'border-amber-300 bg-amber-50 text-amber-800'}`}
          data-testid="nas-preview-pagination-badge">
          ページネーション / Pagination {pagination ? '対応 / supported' : '未対応 / not supported'}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        <Button onClick={() => onStart(MODE_FULL)}
          disabled={busy || !fullAvailable}
          className="h-8 text-[12px] bg-teal-700 hover:bg-teal-800 disabled:opacity-50"
          title={fullAvailable ? undefined : (full?.unavailable_reason_ja || '')}
          data-testid="nas-preview-full-btn"
          data-available={fullAvailable ? 'true' : 'false'}>
          <ListChecks size={13} className="mr-1" weight="bold" />
          {busy && activeMode === MODE_FULL ? '確認中…' : '完全検証プレビュー / Full validation'}
        </Button>
        <Button onClick={() => onStart(MODE_ESTIMATE)}
          disabled={busy || !estimateAvailable}
          variant="outline" className="h-8 text-[12px]"
          data-testid="nas-preview-estimate-btn"
          title={estimateAvailable ? undefined : (estimate?.unavailable_reason_ja || '')}
          data-available={estimateAvailable ? 'true' : 'false'}>
          <Eye size={13} className="mr-1" />
          {busy && activeMode === MODE_ESTIMATE ? '確認中…' : '簡易見積り / Quick estimate'}
        </Button>
      </div>

      {!fullAvailable && (
        <p className="mt-2 text-[11.5px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2.5 py-2"
          data-testid="nas-preview-full-unavailable"
          data-reason={full?.unavailable_reason || 'agent_unreachable_for_preview'}>
          <Warning size={13} weight="fill" className="inline mr-1" />
          {full?.unavailable_reason_ja
            || 'NAS エージェントに接続できないため、完全検証プレビューは実行できません。'}
          <span className="block font-normal opacity-80">
            {full?.unavailable_reason_en
              || 'The NAS agent is unreachable, so a full validation preview cannot run.'}
          </span>
          {full?.unavailable_reason && (
            <span className="block mt-1 font-mono text-[10.5px] opacity-70">
              typed reason: {full.unavailable_reason}
              {full.unavailable_reason === 'pagination_agent_required'
                ? ` · 必要バージョン / required v${modes?.required_agent_version_for_full || '1.3.2'}`
                : ''}
            </span>
          )}
        </p>
      )}

      <p className="mt-2 text-[11px] text-zinc-600" data-testid="nas-preview-mode-note">
        <Info size={13} className="inline mr-1 text-zinc-400" />
        取り込み（インデックス化）を許可できるのは<b>完全検証プレビュー</b>だけです。
        {estimate?.note_ja || '簡易見積りは有界のサンプル取得です。取り込みは許可しません。'}
        <span className="block opacity-80">
          Only a full validation preview can authorise ingestion.{' '}
          {estimate?.note_en || 'A quick estimate is a bounded sample and never authorises ingestion.'}
        </span>
      </p>
    </div>
  );
}

export default NasPreviewModePanel;
